import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { stakeholdersKeys } from "@/application/query-keys/stakeholders.keys";
import {
  stakeholdersRepository,
  type StakeholderFilters,
} from "@/infrastructure/repositories/stakeholders.supabase.repository";
import type { StakeholderRow } from "@/infrastructure/supabase/database.types";

export function useStakeholdersList(filters: StakeholderFilters = {}) {
  return useQuery({
    queryKey: stakeholdersKeys.list(filters),
    queryFn: () => stakeholdersRepository.list(filters),
  });
}

export function useStakeholderDetail(id: string | undefined) {
  return useQuery({
    queryKey: stakeholdersKeys.detail(id ?? ""),
    queryFn: () => stakeholdersRepository.byId(id as string),
    enabled: !!id,
  });
}

export function useCreateStakeholder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<StakeholderRow, "id" | "created_at" | "updated_at">) =>
      stakeholdersRepository.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stakeholdersKeys.all });
    },
  });
}

export function useUpdateStakeholder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<StakeholderRow> }) =>
      stakeholdersRepository.update(id, patch),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: stakeholdersKeys.all });
      queryClient.setQueryData(stakeholdersKeys.detail(data.id), data);
    },
  });
}

export function useDeleteStakeholder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => stakeholdersRepository.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stakeholdersKeys.all });
    },
  });
}
